import { Button, Card, Col, Row, Space, Tabs, Tag, Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import CopyableContent from "../../../../components/copyableContent";
import LockSwitch from "../../../../components/form/lockSwitch";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import PopoverList from "../../../../components/popups/popoverList";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import TaxFlowStore from "../../../../contexts/taxFlowStore";
import _t from "../../../../lang/translate";
import { createTaxScenario, saveTaxScenario } from "../../../../services/taxScenarioService";
import { Vehicle } from "../../../../types/appTypes";
import { TaxCase, TaxScenario } from "../../../../types/taxTypes";
import format from "../../../../utilities/formatNumbers";
import { isValidNum } from "../../../../utilities/typeGuard";
import TaxOfferArchived from "../admin/taxOfferArchived";
import TaxFlowDocumentation from "./taxFlowDocumentation";
import TaxFlowNewPrice from "./taxFlowNewPrice";
import TaxFlowOffer from "./taxFlowOffer";
import TaxFlowOptimization from "./taxFlowOptimization";
import TaxFlowTradePrice from "./taxFlowTradePrice";
import useTaxFlowCalculations from "./useTaxFlowCalculations";
import { getScenarioUpsertPayload } from "./util";
import Icon from "../../../../assets/icons/icon";
import PopMaybeOver from "../../../../components/popMaybeOver";


type Props = {
  optMode: boolean;
}
export default function TaxFlow({ optMode }: Props) {
  const { taxCase } = TaxCaseStore.useContainer()
  const [refreshKey, setRefreshKey] = useState<number>(0)
  const key = optMode ? "optimization" : "offer"
  const taxScenario = taxCase?.scenarios?.[key]
  const initialState = useMemo(() => ({
    taxFlow: taxScenario?.calculation ?? undefined,
    optimization: optMode
  }), [taxScenario?.id, refreshKey])
  const refresh = () => setRefreshKey((prev) => prev + 1)

  return (
    <TaxFlowStore.Provider initialState={initialState}>
      <TaxFlowInner optMode={optMode} refresh={refresh} />
    </TaxFlowStore.Provider>
  )
}

function checkCanSaveNewVersion(scenario: TaxScenario | null, taxCase: TaxCase) {
  return true
}
function checkCanSave(scenario: TaxScenario | null, vehicle: Vehicle | null, scenarioKeyVehicleFields: string): [boolean, string[]] {
  let reasons: string[] = []
  if (!scenario) reasons.push("Intet scenario eksisterer.")
  else if (!scenario.version) reasons.push("Der eksisterer ikke nogen scenarie version")
  else if (scenario.flags?.locked) reasons.push("Scenariet er låst. Gem i ny version");
  const currentKeyVehicleFields = getKeyVehicleFieldsString(vehicle)
  if (currentKeyVehicleFields !== scenarioKeyVehicleFields) {
    let reason = "Nøgledata fra køretøjet er ændret. Gem i ny version:"
    const fields = scenarioKeyVehicleFields.split("|")
    const currentFields = currentKeyVehicleFields.split("|")
    const valuesThanChanged = [];
    for (let i = 0; i < fields.length; i++) {
      if (fields[i] !== currentFields[i]) {
        valuesThanChanged.push(`${currentFields[i]} vs. ${fields[i]}`)
      }
    }
    reason += "\r\n";
    reason += valuesThanChanged.join("\r\n")
    reasons.push(reason)
  }

  return [reasons.length === 0, reasons]
}
function getKeyVehicleFieldsString(vehicle: Vehicle | null) {
  const keyFields = [
    vehicle?.vin ?? "",
    format.date(vehicle?.firstRegDate),
    format.milage(vehicle?.mileage),
    format.emission(vehicle?.emissionAmount),
    vehicle?.emissionUnit ?? "",
    vehicle?.isNew ? "Ny" : "Brugt",
    vehicle?.type ?? ""
  ]
  return keyFields.join("|")
}

type InnerProps = {
  optMode: boolean;
  refresh: () => void;
}
function TaxFlowInner({ optMode, refresh }: InnerProps) {
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer()
  const { taxFlow, setDirty, taxScenario, caseLocked, caseLockedLogicEnabled, setCaseLockedOverride, scenarioKey, mountedDATEquipment } = TaxFlowStore.useContainer()
  useTaxFlowCalculations(optMode)
  const newPriceTotal = taxFlow.newPrice?.newPrice;
  const taxableValue = taxFlow.newPrice?.taxableValue
  const [activeKey, setActiveKey] = useState<string>("newPrice")
  const [saving, setSaving] = useState(false)

  const canSaveNewVersion = checkCanSaveNewVersion(taxScenario ?? null, taxCase)
  const keyVehicleFieldsRef = useRef(getKeyVehicleFieldsString(taxCase.vehicle))
  const [canSave, canSaveDisabledReasons] = checkCanSave(taxScenario ?? null, taxCase.vehicle, keyVehicleFieldsRef.current)

  useEffect(() => {
    if (taxCase.vehicle?.isNew && activeKey === 'tradePrice') {
      setActiveKey("newPrice")
    }
  }, [taxCase.vehicle?.isNew])


  async function handleSave() {
    try {
      if (!taxCase.scenarios?.[scenarioKey] || !taxScenario?.id) {
        throw new Error("Gem i ny version")
      }
      setSaving(true)
      const payload = getScenarioUpsertPayload(taxFlow, optMode)
      const { data: newScenario } = await saveTaxScenario(taxCase.id, taxScenario?.id, payload)
      setTaxCase({
        ...taxCase,
        scenarios: {
          ...taxCase.scenarios,
          [scenarioKey]: newScenario
        }
      })
      if (activeKey === 'tradePrice') {
        setActiveKey("registrationFee")
      }
      setDirty(false)
      refresh()
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveNewVersion() {
    try {
      setSaving(true)
      const payload = getScenarioUpsertPayload(taxFlow, optMode)
      const { data: newTaxCase } = await createTaxScenario(taxCase.id, payload)
      keyVehicleFieldsRef.current = getKeyVehicleFieldsString(taxCase.vehicle)
      setTaxCase(newTaxCase)
      if (activeKey === 'tradePrice') {
        setActiveKey("registrationFee")
      }
      setDirty(false)
      refresh()
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setSaving(false)
    }
  }

  function copyOfferToOptimization() {
    const offer = taxCase.scenarios.offer;
    const newOptimizationScenario: TaxScenario | null = offer ? {
      flags: {
        optimization: true,
        locked: true,
      },
      ...taxCase.scenarios.optimization,
      calculation: {
        ...offer.calculation,
        registrationFee: {
          ...offer.calculation?.registrationFee,
          registrationFee: undefined,
        }

      }
    } : null;

    setTaxCase({
      ...taxCase,
      scenarios: {
        offer,
        optimization: newOptimizationScenario
      }
    })
    setDirty(true)
    refresh()
  }


  const newPriceWarningMessage = useMemo(() => {
    return mountedDATEquipment?.map(eq => {
      return eq.code
    }).join(", ")
  }, [mountedDATEquipment])

  return (
    <div>
      <Card
        title={
          <Row justify="space-between" align={"middle"} className="mr-2">
            <Col>
              {_t(optMode ? "evaluation" : "preassessment")}
            </Col>
            <Col>
              <Space>
                {caseLockedLogicEnabled && (
                  <span onClick={e => e.stopPropagation()}>
                    <LockSwitch value={caseLocked} onChange={() => setCaseLockedOverride(!caseLocked)} />
                  </span>
                )}
                <TaxFlowStatus />
              </Space>
            </Col>
          </Row>
        }
        className="tax-flow-card"
      >
        <Tabs activeKey={activeKey} onChange={setActiveKey}>
          <Tabs.TabPane key="newPrice" forceRender tab={
            <TabPaneTitle
              warningMessage={newPriceWarningMessage}
              warningTitle={newPriceWarningMessage.length ? `Der er monteret DAT udstyr (${mountedDATEquipment.length})` : undefined}
              text={taxCase.vehicle?.isNew ? _t("taxable_value") : _t("new_price")}
              price={taxCase.vehicle?.isNew ? taxableValue ?? null : newPriceTotal ?? null}
            />
          }>
            <TaxFlowNewPrice isOptimzation={optMode} />
          </Tabs.TabPane>
          {!taxCase.vehicle?.isNew && (
            <Tabs.TabPane key="tradePrice" forceRender disabled={!!taxCase.vehicle?.isNew} tab={
              <TabPaneTitle
                text={_t("trade_price")}
                price={taxFlow.tradePrice?.tradePrice ?? null}
              />
            }>
              <TaxFlowTradePrice />
            </Tabs.TabPane>
          )}
          <Tabs.TabPane key="registrationFee" forceRender tab={
            <TabPaneTitle
              text={_t("registration_fee")}
              price={taxFlow.registrationFee?.registrationFee ?? null}
            />
          }>
            {optMode ? <TaxFlowOptimization /> : <TaxFlowOffer />}
          </Tabs.TabPane>
          <Tabs.TabPane key="documentation" forceRender tab={_t("documentation")}>
            <TaxFlowDocumentation />
          </Tabs.TabPane>
        </Tabs>

        <div style={{
          marginTop: 24,
          position: "sticky",
          bottom: 0,
          background: "white",
          zIndex: 3,
          boxShadow: "0 -2px 6px -4px rgba(0, 0, 0, 0.1)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
        }}>
          <Row justify="space-between" className="p-1">
            <Col>
              <Space>
                <TaxOfferArchived optMode={optMode} />
                {optMode && <Button disabled={caseLocked} onClick={copyOfferToOptimization}>{_t("copy")}</Button>}
              </Space>
            </Col>
            <Col>
              <Space>
                <small className="text-italic">
                  {_t("latest_change_by")} {taxScenario?.person?.fullName} • {format.dateAndTime(taxScenario?.updatedAt)}
                </small>
                <Button loading={saving} onClick={handleSaveNewVersion}
                  type={taxScenario?.flags?.locked ? "primary" : "default"}
                  icon={<Icon name="duplicate-outline" />}
                  disabled={!canSaveNewVersion || caseLocked}
                >{_t("save_in_new_version")}</Button>
                <PopoverList list={canSaveDisabledReasons}>
                  <Button loading={saving} onClick={handleSave}
                    type="primary" disabled={!canSave || caseLocked}
                  >{_t("update")}</Button>
                </PopoverList>
              </Space>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  )
}

export function TabPaneTitle({ text, price, warningMessage, warningTitle }: { text: string; price: number | null, warningMessage?: string, warningTitle?: string }) {
  return (
    <PopMaybeOver title={warningTitle} content={warningMessage}>
      <Space className={warningTitle ? "text-danger" : ""}>
        <span className="pane-label">{text}</span>
        {isValidNum(price) && (
          <CopyableContent contentToCopy={Math.ceil(price).toString()}>
            <Tag className="ant-tag-exception">{format.price(price)}</Tag>
          </CopyableContent>
        )}
      </Space>
    </PopMaybeOver>
  )
}


function TaxFlowStatus() {
  const { dirty, taxScenario } = TaxFlowStore.useContainer()
  function determineSaveState() {
    switch (dirty) {
      case false: return _t("saved");
      case true: return _t("not_saved");
      default: return _t("not_saved");
    }
  }
  const saveState = determineSaveState()
  function determineColor() {
    if (dirty) {
      return "warning"
    } else {
      return "success"
    }
  }
  const color = determineColor()
  function deriveVersion() {
    const version = taxScenario?.version
    return `v${version ?? "0"}`
  }
  const version = deriveVersion()

  return (
    <Tag className="ant-tag-exception" style={{ borderRadius: 20 }} color={color}>
      <span>{version}</span>
      {' '}&bull;{' '}
      <span>{saveState}</span>
    </Tag>

  )
}
